import { Injectable, NgZone, inject } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  private readonly zone = inject(NgZone);
  public menuCollapsed: boolean = false;
  public menuHover: boolean = false;
  public titulo$: Subject<{ nome: string, pathRetorno: string }>;

  // Menu recolhido por padrão em tela de notebook (1200 a 1439 px, ex.: 1366): devolve à grade a
  // largura que fazia o status sumir à direita. Abaixo de 1200 o tema já usa o menu sobreposto. A
  // escolha do usuário (botão de recolher, só no desktop) fica no navegador e vence a regra.
  private static readonly DesktopMinWidth = 1200;
  private static readonly AutoCollapseBelowWidth = 1440;
  private static readonly CollapsedStorageKey = 'mc_menu_collapsed';

  constructor() {
    this.titulo$ = new Subject<{ nome: string, pathRetorno: string }>();
    this.applyInitialCollapse();
  }

  private applyInitialCollapse() {
    if (typeof window === 'undefined' || window.innerWidth < LayoutService.DesktopMinWidth) return;
    const saved = LayoutService.readCollapsedChoice();
    const collapse = saved ?? window.innerWidth < LayoutService.AutoCollapseBelowWidth;
    if (!collapse) return;
    this.menuCollapsed = true;
    document.querySelector('html')?.classList.add('layout-menu-collapsed');
  }

  private static readCollapsedChoice(): boolean | null {
    try {
      const value = localStorage.getItem(LayoutService.CollapsedStorageKey);
      return value === null ? null : value === '1';
    } catch {
      return null;
    }
  }

  private static saveCollapsedChoice(collapsed: boolean) {
    try {
      localStorage.setItem(LayoutService.CollapsedStorageKey, collapsed ? '1' : '0');
    } catch {
      // Sem armazenamento (modo privado): a escolha vale só nesta sessão.
    }
  }

  // Intenção de hover no menu recolhido: abre só depois de o mouse PARAR em cima (passada de
  // raspão a caminho da página não abre) e fecha com uma folga curta (sair por 1px na borda não
  // fecha e reabre). Sem isso o menu piscava aberto a cada passada.
  private static readonly HoverOpenDelayMs = 150;
  private static readonly HoverCloseDelayMs = 120;
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;

  private clearHoverTimer() {
    if (this.hoverTimer) clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
  }

  public menuEnter() {
    this.clearHoverTimer();
    if (!document.querySelector("html")?.classList.contains('layout-menu-collapsed')) return;
    this.hoverTimer = setTimeout(() => this.openHover(), LayoutService.HoverOpenDelayMs);
  }

  public menuLeave() {
    this.clearHoverTimer();
    this.hoverTimer = setTimeout(() => this.closeHover(), LayoutService.HoverCloseDelayMs);
  }

  private openHover() {
    this.hoverTimer = null;
    if (document.querySelector("html")?.classList.contains('layout-menu-collapsed')) {
      // O hover chega de fora da zona: entra nela só para o template ver a mudança.
      if (!this.menuHover) this.zone.run(() => (this.menuHover = true));
      if (!document.querySelector("html")?.classList.contains('layout-menu-hover')) {
        document.querySelector("html")?.classList.add('layout-menu-hover');
        document.querySelector(".layout-menu-toggle")?.classList.add('d-block');
      }
    }
  }

  private closeHover() {
    this.hoverTimer = null;
    if (this.menuHover) this.zone.run(() => (this.menuHover = false));
    if (document.querySelector("html")?.classList.contains('layout-menu-collapsed') &&
      document.querySelector("html")?.classList.contains('layout-menu-hover')) {
      document.querySelector("html")?.classList.remove('layout-menu-hover');
      document.querySelector(".layout-menu-toggle")?.classList.remove('d-block');
    }
  }

  public menuToggle() {
    this.clearHoverTimer();
    this.menuCollapsed = !this.menuCollapsed;
    this.menuHover = false;
    document.querySelector("html")?.classList.toggle('layout-menu-collapsed');
    document.querySelector("html")?.classList.remove('layout-menu-hover');
    // Só o botão do desktop é escolha de layout; no celular o mesmo toggle abre e fecha o menu sobreposto.
    if (window.innerWidth >= LayoutService.DesktopMinWidth) LayoutService.saveCollapsedChoice(this.menuCollapsed);
  }
}
